import * as THREE from 'three'

import ProjectBoardMaterial from '../../Materials/ProjectBoard.js'
import gsap from 'gsap'
import palette from '../../../content/palette.js'

export default class Project
{
    constructor(_options)
    {
        // Options
        this.time = _options.time
        this.resources = _options.resources
        this.objects = _options.objects
        this.areas = _options.areas
        this.name = _options.name
        this.meshes = _options.meshes
        this.debug = _options.debug
        this.name = _options.name
        this.x = _options.x
        this.y = _options.y
        this.imageSources = _options.imageSources
        this.label = _options.label
        this.blockText = _options.blockText
        this.link = _options.link

        // Set up
        this.container = new THREE.Object3D()
        this.container.matrixAutoUpdate = false
        // this.container.updateMatrix()

        this.setBoards()
        this.setFloor()
        this.setFloorPrint()
        this.setLabel()
    }

    setBoards()
    {
        // Set up
        this.boards = {}
        this.boards.items = []
        this.boards.xStart = - 5
        this.boards.xInter = 5
        this.boards.y = 5
        this.boards.color = palette.boardColor
        this.boards.threeColor = new THREE.Color(this.boards.color)

        if(this.debug)
        {
            this.debug.addColor(this.boards, 'color').name('boardColor').onChange(() =>
            {
                this.boards.threeColor.set(this.boards.color)
            })
        }

        // Create each board
        let i = 0

        for(const _imageSource of this.imageSources)
        {
            // Set up
            const board = {}
            board.x = this.x + this.boards.xStart + i * this.boards.xInter
            board.y = this.y + this.boards.y

            // Create structure with collision
            this.objects.add({
                base: this.resources.items.projectsBoardStructure.scene,
                collision: this.resources.items.projectsBoardCollision.scene,
                floorShadowTexture: this.resources.items.projectsBoardStructureFloorShadowTexture,
                offset: new THREE.Vector3(board.x, board.y, 0),
                rotation: new THREE.Euler(0, 0, 0),
                duplicated: true,
                mass: 0
            })

            // Image load
            const image = new Image()
            image.addEventListener('load', () =>
            {
                board.texture = new THREE.Texture(image)
                // board.texture.magFilter = THREE.NearestFilter
                // board.texture.minFilter = THREE.LinearFilter
                board.texture.anisotropy = 4
                // board.texture.colorSpace = THREE.SRGBColorSpace
                board.texture.needsUpdate = true

                board.planeMesh.material.uniforms.uTexture.value = board.texture

                gsap.to(board.planeMesh.material.uniforms.uTextureAlpha, { value: 1, duration: 1, ease: 'power4.inOut' })
            })

            image.src = _imageSource

            // Plane
            board.planeMesh = this.meshes.boardPlane.clone()
            board.planeMesh.position.x = board.x
            board.planeMesh.position.y = board.y
            board.planeMesh.matrixAutoUpdate = false
            board.planeMesh.updateMatrix()
            board.planeMesh.material = new ProjectBoardMaterial()
            board.planeMesh.material.uniforms.uColor.value = this.boards.threeColor
            board.planeMesh.material.uniforms.uTextureAlpha.value = 0
            this.container.add(board.planeMesh)

            // Save
            this.boards.items.push(board)

            i++
        }
    }

    setFloor()
    {
        this.floor = {}

        this.floor.x = 0
        this.floor.y = - 2

        // Container
        this.floor.container = new THREE.Object3D()
        this.floor.container.position.x = this.x + this.floor.x
        this.floor.container.position.y = this.y + this.floor.y
        this.floor.container.matrixAutoUpdate = false
        this.floor.container.updateMatrix()
        this.container.add(this.floor.container)

        // Area (only when the project has a link)
        if(this.link)
        {
            this.floor.area = this.areas.add({
                position: new THREE.Vector2(this.x + this.link.x, this.y + this.floor.y + this.link.y),
                halfExtents: new THREE.Vector2(this.link.halfExtents.x, this.link.halfExtents.y)
            })
            this.floor.area.on('interact', () =>
            {
                window.open(this.link.href, '_blank')
            })

            // Area label
            this.floor.areaLabel = this.meshes.areaLabel.clone()
            this.floor.areaLabel.position.x = this.link.x
            this.floor.areaLabel.position.y = this.link.y
            this.floor.areaLabel.position.z = 0.001
            this.floor.areaLabel.matrixAutoUpdate = false
            this.floor.areaLabel.updateMatrix()
            this.floor.container.add(this.floor.areaLabel)
        }
    }

    setFloorPrint()
    {
        if(!this.imageSources.length)
        {
            return
        }

        // Son görsel zemine basılır (pano oranı 1.72:1), etiketin önünde, sıra yolunun güneyinde
        this.floorPrint = {}
        this.floorPrint.width = 7.2
        this.floorPrint.height = this.floorPrint.width / (4.671 / 2.714)

        const material = new THREE.MeshBasicMaterial({
            transparent: true,
            opacity: 0,
            depthWrite: false,
            alphaMap: this.meshes.printMask
        })

        const image = new Image()
        image.addEventListener('load', () =>
        {
            const texture = new THREE.Texture(image)
            texture.colorSpace = THREE.SRGBColorSpace
            texture.anisotropy = 4
            texture.needsUpdate = true
            material.map = texture
            material.needsUpdate = true
            gsap.to(material, { opacity: 0.92, duration: 1, ease: 'power4.inOut' })
        })
        image.src = this.imageSources[this.imageSources.length - 1]

        this.floorPrint.mesh = new THREE.Mesh(new THREE.PlaneGeometry(this.floorPrint.width, this.floorPrint.height), material)
        this.floorPrint.mesh.position.set(this.x - 1, this.y - 3.3, 0.005)
        this.floorPrint.mesh.matrixAutoUpdate = false
        this.floorPrint.mesh.updateMatrix()
        this.floorPrint.material = material
        this.container.add(this.floorPrint.mesh)
    }

    setLabel()
    {
        if(!this.label)
        {
            return
        }

        // Devrilebilen 3D harfler: zeminin sol kenarından başlar, panoların önünde durur
        this.labelText = this.blockText.add(this.label, {
            x: this.x - 8,
            y: this.y + 0.8
        })
    }
}
